/**
 * eSocial Service
 * API integration for eSocial event management
 */

import apiClient from './apiClient';
import {
  ESocialEvent,
  ESocialEventLog,
  ESocialDashboard,
  ESocialEventFilters,
  CreateESocialEventRequest,
  UpdateESocialEventRequest,
  ESocialEventStatus
} from '../types/esocial';

export interface ESocialEventListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  filters?: ESocialEventFilters;
}

export interface ESocialEventListResponse {
  events: ESocialEvent[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * eSocial Service Class
 */
class ESocialService {
  private getBaseUrl(companyId: string): string {
    return `/companies/${companyId}/esocial`;
  }

  /**
   * Get paginated list of events for a company
   */
  async getEvents(
    companyId: string,
    params: ESocialEventListParams = {}
  ): Promise<ESocialEventListResponse> {
    const { page = 1, limit = 20, sortBy = 'eventDate', sortOrder = 'DESC', filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder
    });

    if (filters) {
      if (filters.eventType) {
        const types = Array.isArray(filters.eventType)
          ? filters.eventType.join(',')
          : filters.eventType;
        queryParams.append('eventType', types);
      }
      if (filters.eventStatus) {
        const statuses = Array.isArray(filters.eventStatus)
          ? filters.eventStatus.join(',')
          : filters.eventStatus;
        queryParams.append('eventStatus', statuses);
      }
      if (filters.employeeId) {
        queryParams.append('employeeId', filters.employeeId.toString());
      }
      if (filters.dateFrom) {
        queryParams.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        queryParams.append('dateTo', filters.dateTo);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
    }

    const response = await apiClient.get(
      `${this.getBaseUrl(companyId)}/events?${queryParams}`
    );

    return response.data;
  }

  /**
   * Get a single event by ID
   */
  async getEventById(companyId: string, eventId: string): Promise<ESocialEvent> {
    const response = await apiClient.get(
      `${this.getBaseUrl(companyId)}/events/${eventId}`
    );
    return response.data;
  }

  /**
   * Create a new event
   */
  async createEvent(
    companyId: string,
    data: CreateESocialEventRequest
  ): Promise<ESocialEvent> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events`,
      data
    );
    return response.data;
  }

  /**
   * Update an existing event
   */
  async updateEvent(
    companyId: string,
    eventId: string,
    data: UpdateESocialEventRequest
  ): Promise<ESocialEvent> {
    const response = await apiClient.put(
      `${this.getBaseUrl(companyId)}/events/${eventId}`,
      data
    );
    return response.data;
  }

  /**
   * Delete an event (only DRAFT status)
   */
  async deleteEvent(companyId: string, eventId: string): Promise<void> {
    await apiClient.delete(`${this.getBaseUrl(companyId)}/events/${eventId}`);
  }

  /**
   * Submit an event for transmission
   */
  async submitEvent(companyId: string, eventId: string): Promise<ESocialEvent> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/${eventId}/submit`
    );
    return response.data;
  }

  /**
   * Cancel an event
   */
  async cancelEvent(companyId: string, eventId: string): Promise<ESocialEvent> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/${eventId}/cancel`
    );
    return response.data;
  }

  /**
   * Get event logs
   */
  async getEventLogs(companyId: string, eventId: string): Promise<ESocialEventLog[]> {
    const response = await apiClient.get(
      `${this.getBaseUrl(companyId)}/events/${eventId}/logs`
    );
    return response.data;
  }

  /**
   * Get event statistics for a company
   */
  async getStats(companyId: string): Promise<Record<ESocialEventStatus, number>> {
    const response = await apiClient.get(`${this.getBaseUrl(companyId)}/stats`);
    return response.data;
  }

  /**
   * Get eSocial dashboard data for a company
   */
  async getDashboard(companyId: string): Promise<ESocialDashboard> {
    const response = await apiClient.get(`${this.getBaseUrl(companyId)}/dashboard`);
    return response.data;
  }

  /**
   * Transmit an event to the government (build XML, sign, send via SOAP)
   */
  async transmitEvent(companyId: string, eventId: string): Promise<{ success: boolean; protocoloEnvio?: string; error?: string }> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/${eventId}/transmit`
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Retry a failed/rejected event
   */
  async retryEvent(companyId: string, eventId: string): Promise<ESocialEvent> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/${eventId}/retry`
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Batch transmit multiple events
   */
  async batchTransmit(companyId: string, eventIds: number[]): Promise<{ success: boolean; eventCount: number; errors?: Array<{ eventId: number; error: string }> }> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/batch-transmit`,
      { eventIds }
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Poll for transmission results by protocol number
   */
  async pollResults(companyId: string, protocolNumber: string): Promise<{ status: string; events?: Array<{ eventId: number; status: string }> }> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/poll/${protocolNumber}`
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Preview the XML that would be generated for an event
   */
  async previewXml(companyId: string, eventId: string): Promise<{ xml: string; validation: { valid: boolean; errors?: Array<{ message: string }> } }> {
    const response = await apiClient.get(
      `${this.getBaseUrl(companyId)}/events/${eventId}/xml-preview`
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Generate S-2240 events in batch for multiple employees
   */
  async generateS2240Batch(companyId: string, employeeIds: number[]): Promise<{ created: number; drafts: number; skipped: number; errors: Array<{ employeeId: number; error: string }> }> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/generate-s2240`,
      { employeeIds }
    );
    return response.data?.data ?? response.data;
  }

  /**
   * Generate S-2220 (ASO) events in batch for multiple employees
   */
  async generateS2220Batch(companyId: string, employeeIds: number[]): Promise<{ created: number; drafts: number; skipped: number; errors: Array<{ employeeId: number; error: string }> }> {
    const response = await apiClient.post(
      `${this.getBaseUrl(companyId)}/events/generate-s2220`,
      { employeeIds }
    );
    return response.data?.data ?? response.data;
  }
}

export const esocialService = new ESocialService();
export default esocialService;
